import { Component } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

// Fehlerschranke gegen den weissen Bildschirm.
//
// Bis zum 01.08.2026 gab es keine: Warf irgendeine Komponente beim Rendern, riss
// React den gesamten Baum ab. Der Nutzer sah eine LEERE SEITE — ohne Erklaerung,
// ohne Weg zurueck — und der Betreiber erfuhr nichts davon. Im Audit ergab die
// Suche nach `ErrorBoundary`, `componentDidCatch` und `Sentry` im ganzen
// `frontend/src` null Treffer.
//
// Nur Klassenkomponenten koennen Renderfehler fangen (`getDerivedStateFromError`
// / `componentDidCatch` haben keine Hook-Entsprechung). Deshalb hier eine Klasse,
// obwohl der Rest des Projekts mit Funktionen arbeitet.

function melde(error, info) {
  // Best effort. Schlaegt das Melden fehl, ist das egal — der Nutzer sieht
  // trotzdem die Auffangseite. Ein Fehler IM Fehlerbericht darf den Bericht
  // nicht zu einem zweiten Absturz machen.
  try {
    const daten = {
      message: String(error?.message || error || "Unbekannter Fehler").slice(0, 500),
      stack: String(error?.stack || "").slice(0, 4000),
      component_stack: String(info?.componentStack || "").slice(0, 4000),
      url: String(window.location.href).slice(0, 500),
      user_agent: String(navigator.userAgent || "").slice(0, 300),
    };
    // Absichtlich `fetch` und nicht die axios-Instanz: Die haengt an
    // Interceptoren, die bei einem kaputten Zustand selbst scheitern koennten.
    // `keepalive`, damit die Meldung auch dann rausgeht, wenn der Nutzer die
    // Seite sofort neu laedt.
    fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(daten),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* nichts — siehe oben */
  }
}

// Die sichtbare Auffangseite. Bewusst schlicht und ohne Bildmaterial: Sie muss
// auch dann noch stehen, wenn der Grund des Absturzes irgendwo im Layout liegt.
function Auffangseite({ onReload }) {
  const { t } = useLang();
  return (
    <div className="max-w-xl mx-auto px-6 py-20 sm:py-28 text-center" data-testid="error-boundary">
      <div className="kicker text-terracotta">{t("crash.overline")}</div>
      <h1 className="font-heading text-3xl sm:text-4xl text-anthracite mt-2" style={{ textWrap: "balance" }}>
        {t("crash.title")}
      </h1>
      <p className="text-anthracite/70 mt-3">{t("crash.text")}</p>
      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <button
          type="button"
          onClick={onReload}
          data-testid="error-reload"
          className="h-12 px-6 rounded-control bg-terracotta hover:bg-terracotta-hover text-white text-sm font-medium inline-flex items-center transition-colors"
        >
          {t("crash.retry")}
        </button>
        {/* Bewusst ein echtes <a> und kein <Link>: Ein harter Seitenaufruf baut
            den React-Baum neu auf. Ein Router-Wechsel wuerde denselben
            kaputten Zustand behalten, wenn der Fehler ausserhalb der Routen
            liegt. */}
        <a
          href="/"
          data-testid="error-home"
          className="h-12 px-6 rounded-control border border-anthracite/20 text-anthracite text-sm inline-flex items-center hover:border-anthracite transition-colors"
        >
          {t("crash.home")}
        </a>
      </div>
    </div>
  );
}

// Notfall-Auffangseite OHNE i18n und ohne Projekt-Komponenten.
// Fuer die aeussere Schranke: Wirft der LanguageProvider selbst, gaebe es kein
// `t()` mehr — eine Auffangseite, die dann auch abstuerzt, waere wertlos.
// Deshalb hier feste englische Woerter (Fallback-Sprache des Projekts) und
// reines Inline-CSS, unabhaengig davon, ob Tailwind geladen ist.
function NotAuffangseite() {
  return (
    <div
      data-testid="error-boundary-minimal"
      style={{
        maxWidth: "36rem", margin: "0 auto", padding: "5rem 1.5rem",
        textAlign: "center", fontFamily: "system-ui, sans-serif", color: "#2d2d2d",
      }}
    >
      {/* i18n-ignore: absichtlich fest — laeuft, wenn der LanguageProvider selbst wirft */}
      <h1 style={{ fontSize: "1.75rem", margin: "0 0 .75rem" }}>Something went wrong</h1>
      <p style={{ opacity: 0.7, margin: "0 0 2rem" }}>
        {/* i18n-ignore: siehe oben */}
        Please reload the page. If the problem persists, try again later.
      </p>
      <a
        href="/"
        style={{
          display: "inline-block", padding: ".75rem 1.5rem", borderRadius: ".5rem",
          background: "#BC4A2B", color: "#fff", textDecoration: "none", fontSize: ".875rem",
        }}
      >
        {/* i18n-ignore: siehe oben */}
        Back to homepage
      </a>
    </div>
  );
}

class Schranke extends Component {
  constructor(props) {
    super(props);
    this.state = { gefallen: false };
  }

  static getDerivedStateFromError() {
    return { gefallen: true };
  }

  componentDidCatch(error, info) {
    melde(error, info);
    // In der Entwicklung zusaetzlich sichtbar lassen — sonst sucht man den
    // Fehler in einer huebschen Auffangseite statt in der Konsole.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary hat einen Renderfehler gefangen:", error, info);
    }
  }

  componentDidUpdate(prevProps) {
    // Nach einem Seitenwechsel wieder aufmachen: Stuerzt EINE Seite ab, soll die
    // naechste normal rendern. Ohne das bliebe die Auffangseite stehen, bis der
    // Nutzer neu laedt — auch auf Seiten, die voellig in Ordnung sind.
    if (this.state.gefallen && prevProps.zuruecksetzenBei !== this.props.zuruecksetzenBei) {
      this.setState({ gefallen: false });
    }
  }

  render() {
    if (!this.state.gefallen) return this.props.children;
    if (this.props.minimal) return <NotAuffangseite />;
    return <Auffangseite onReload={() => window.location.reload()} />;
  }
}

/** Aeussere Schranke: faengt auch Fehler aus den Providern. Ohne i18n. */
export function NotSchranke({ children }) {
  return <Schranke minimal>{children}</Schranke>;
}

/** Innere Schranke um die Routen. Behaelt Kopf- und Fusszeile, damit der Nutzer
 *  weiterklicken kann, und macht bei einem Seitenwechsel wieder auf. */
export default function ErrorBoundary({ children }) {
  const location = useLocation();
  return <Schranke zuruecksetzenBei={location.pathname}>{children}</Schranke>;
}
